import { useState, useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  sharePlatforms,
  referralTypes,
  referralRecords,
  referrerProfile,
  referralRewards,
  topReferrers,
  mostSharedEntities,
  shareDashboardStats,
  referralScoreActions,
  referralStatusConfig,
  formatShareCount,
  formatConversionRate,
  getPlatformIcon,
  getPlatformLabel,
  getPlatformColor,
  type SharePlatform,
  type ReferralType,
  type ReferralStatus,
  type ReferralRecord,
} from '@/mocks/rfwShares';

type PageState = 'loading' | 'error' | 'ready';
type ViewTab = 'dashboard' | 'compartir' | 'referidos' | 'recompensas' | 'ranking';

export default function CompartidosPage() {
  const [pageState, setPageState] = useState<PageState>('loading');
  const [viewTab, setViewTab] = useState<ViewTab>('dashboard');
  const [referralFilter, setReferralFilter] = useState<ReferralType | 'todos'>('todos');
  const [statusFilter, setStatusFilter] = useState<ReferralStatus | 'todos'>('todos');
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteType, setInviteType] = useState<ReferralType>('invitar_amigo');
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [sendSuccess, setSendSuccess] = useState(false);
  const [selectedReward, setSelectedReward] = useState<string | null>(null);
  const [shareCopied, setShareCopied] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setPageState('ready'), 500);
    return () => clearTimeout(t);
  }, []);

  const handleRetry = useCallback(() => {
    setPageState('loading');
    setTimeout(() => setPageState('ready'), 500);
  }, []);

  const filteredRecords = useMemo(() => {
    let result = referralRecords;
    if (referralFilter !== 'todos') result = result.filter((r) => r.referralType === referralFilter);
    if (statusFilter !== 'todos') result = result.filter((r) => r.status === statusFilter);
    return result;
  }, [referralFilter, statusFilter]);

  const handleInvite = () => {
    if (!inviteName.trim() || !inviteEmail.trim()) return;
    setSendSuccess(true);
    setTimeout(() => {
      setSendSuccess(false);
      setInviteModalOpen(false);
      setInviteName('');
      setInviteEmail('');
      setInviteMessage('');
    }, 2000);
  };

  const handleShare = (platform: SharePlatform) => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    const shareUrls: Record<SharePlatform, string> = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent('¡Descubrí RFW! ' + url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(url)}`,
      x: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent('¡Sumate a RFW! ')}`,
      qr: url,
      copy_link: url,
    };
    if (platform === 'copy_link') {
      navigator.clipboard.writeText(url).then(() => {
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2000);
      }).catch(() => {});
    } else if (platform === 'qr') {
      // QR shown inline
    } else {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  const getStatusBadge = (status: ReferralStatus) => {
    const config = referralStatusConfig[status];
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${config.bgColor} ${config.color}`}>
        <i className={`${config.icon} text-[9px]`}></i>
        {config.label}
      </span>
    );
  };

  const getTypeBadge = (type: ReferralType) => {
    const config = referralTypes.find((t) => t.id === type);
    if (!config) return null;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${config.bgColor} ${config.color}`}>
        <i className={`${config.icon} text-[9px]`}></i>
        {config.label}
      </span>
    );
  };

  const viewTabs: { id: ViewTab; label: string; icon: string; count?: number }[] = [
    { id: 'dashboard', label: 'Dashboard', icon: 'ri-dashboard-line' },
    { id: 'compartir', label: 'Compartir', icon: 'ri-share-forward-line', count: sharePlatforms.length },
    { id: 'referidos', label: 'Mis Referidos', icon: 'ri-user-add-line', count: referralRecords.length },
    { id: 'recompensas', label: 'Recompensas', icon: 'ri-gift-line', count: referralRewards.length },
    { id: 'ranking', label: 'Ranking', icon: 'ri-trophy-line', count: topReferrers.length },
  ];

  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-background-50">
        <section className="relative w-full h-[280px] md:h-[380px] bg-background-200 animate-pulse" />
        <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 space-y-8">
          <div className="flex gap-2 flex-wrap">{[1,2,3,4,5].map((i) => (<div key={i} className="bg-background-100 rounded-full h-8 w-24 animate-pulse" />))}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{[1,2,3,4,5,6].map((i) => (<div key={i} className="bg-background-100 rounded-2xl h-[320px] animate-pulse" />))}</div>
        </main>
      </div>
    );
  }

  if (pageState === 'error') {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center px-4 py-20">
        <div className="w-20 h-20 rounded-3xl bg-red-100 flex items-center justify-center mb-5">
          <i className="ri-error-warning-line text-3xl text-red-500"></i>
        </div>
        <h2 className="text-xl font-extrabold text-foreground-950 mb-2">Error al cargar compartidos</h2>
        <p className="text-sm text-foreground-500 mb-6 text-center max-w-md">No pudimos cargar los datos. Verificá tu conexión e intentá de nuevo.</p>
        <button onClick={handleRetry} className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-xl text-sm font-bold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-refresh-line"></i>Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      {/* HERO */}
      <section className="relative w-full h-[300px] md:h-[420px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Vibrant%20digital%20network%20visualization%2C%20interconnected%20nodes%20and%20people%20sharing%20content%20across%20social%20platforms%2C%20warm%20golden%20and%20coral%20gradient%20atmosphere%2C%20abstract%20connection%20lines%20representing%20community%20growth%2C%20modern%20tech%20aesthetic%20with%20human%20warmth%2C%20editorial%20quality&width=1600&height=900&seq=rfw-compartidos-hero&orientation=landscape"
          alt="Compartidos y Referidos RFW"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
          <div className="w-16 h-16 rounded-2xl bg-background-50/15 backdrop-blur-md flex items-center justify-center mb-4 border border-background-50/20">
            <i className="ri-share-forward-line text-3xl text-background-50"></i>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-background-50 leading-tight mb-3">
            Compartidos y Referidos RFW
          </h1>
          <p className="text-sm md:text-base text-background-50/80 max-w-xl leading-relaxed">
            Convertí usuarios en promotores. Compartí en 6 plataformas, invitá amigos, oyentes, radios y locutores. Cada referido suma Score y te acerca a recompensas VIP.
          </p>
          <div className="flex items-center gap-3 mt-5">
            <span className="text-background-50/60 text-xs">{formatShareCount(shareDashboardStats.totalShares)} compartidos · {formatShareCount(shareDashboardStats.totalInvitations)} invitaciones · {shareDashboardStats.conversionRate}% conversión</span>
          </div>
        </div>
      </section>

      {/* MAIN */}
      <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-12 space-y-8">
        {/* STATS STRIP */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Compartidos Totales', value: formatShareCount(shareDashboardStats.totalShares), icon: 'ri-share-forward-line', color: 'text-primary-600 bg-primary-50' },
            { label: 'Invitaciones', value: formatShareCount(shareDashboardStats.totalInvitations), icon: 'ri-send-plane-line', color: 'text-accent-600 bg-accent-50' },
            { label: 'Aceptadas', value: formatShareCount(shareDashboardStats.acceptedInvitations), icon: 'ri-check-double-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Conversión', value: `${shareDashboardStats.conversionRate}%`, icon: 'ri-line-chart-line', color: 'text-amber-600 bg-amber-50' },
            { label: 'Score Generado', value: formatShareCount(shareDashboardStats.totalScoreGenerated), icon: 'ri-flashlight-line', color: 'text-violet-600 bg-violet-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-background-100 border border-background-200/70">
              <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                <p className="text-[10px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* VIEW TABS */}
        <div className="flex flex-wrap items-center gap-1.5">
          {viewTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setViewTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                viewTab === tab.id ? 'bg-primary-500 text-white' : 'bg-background-100 text-foreground-600 hover:text-foreground-900'
              }`}
            >
              <i className={`${tab.icon} text-[10px]`}></i>
              {tab.label}
              {tab.count !== undefined && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${viewTab === tab.id ? 'bg-white/20 text-white' : 'bg-background-200 text-foreground-500'}`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ═══ DASHBOARD ═══ */}
        {viewTab === 'dashboard' && (
          <div className="space-y-8">
            {/* Perfil del Referidor */}
            <div className="rounded-3xl bg-background-100 border border-background-200/70 p-6 md:p-8">
              <div className="flex flex-col md:flex-row md:items-center gap-5 mb-6">
                <img src={referrerProfile.referrerAvatar} alt={referrerProfile.referrerName} className="w-20 h-20 rounded-full object-cover shrink-0 ring-4 ring-primary-200" />
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-xl font-extrabold text-foreground-950">{referrerProfile.referrerName}</h3>
                    <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-[10px] font-bold">{referrerProfile.referrerTier}</span>
                    <span className="px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-[10px] font-bold">Nivel {referrerProfile.referrerLevel}</span>
                  </div>
                  <p className="text-sm text-foreground-500">Racha de {referrerProfile.currentStreak} semanas · {referrerProfile.totalScoreEarned} pts RFW ganados</p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                {[
                  { label: 'Total Invitaciones', value: referrerProfile.totalInvitations, icon: 'ri-send-plane-line', color: 'text-primary-600 bg-primary-50' },
                  { label: 'Aceptadas', value: referrerProfile.acceptedInvitations, icon: 'ri-check-double-line', color: 'text-emerald-600 bg-emerald-50' },
                  { label: 'Pendientes', value: referrerProfile.pendingInvitations, icon: 'ri-time-line', color: 'text-amber-600 bg-amber-50' },
                  { label: 'Conversión', value: `${referrerProfile.conversionRate}%`, icon: 'ri-line-chart-line', color: 'text-accent-600 bg-accent-50' },
                ].map((stat, i) => (
                  <div key={i} className="text-center p-4 rounded-xl bg-background-50 border border-background-200/70">
                    <div className={`w-10 h-10 rounded-full ${stat.color} flex items-center justify-center mx-auto mb-2`}>
                      <i className={`${stat.icon} text-base`}></i>
                    </div>
                    <p className="text-xl font-extrabold text-foreground-950">{stat.value}</p>
                    <p className="text-[10px] text-foreground-500">{stat.label}</p>
                  </div>
                ))}
              </div>

              {/* Badges */}
              {referrerProfile.badges.length > 0 && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[10px] font-semibold text-foreground-600">Insignias ganadas:</span>
                  {referrerProfile.badges.map((badge) => (
                    <span key={badge} className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-[10px] font-medium">{badge}</span>
                  ))}
                </div>
              )}

              {/* Invited Entities */}
              {referrerProfile.invitedEntities.length > 0 && (
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <span className="text-[10px] font-semibold text-foreground-600">Radios traídas:</span>
                  {referrerProfile.invitedEntities.map((entity) => (
                    <span key={entity} className="px-2 py-0.5 rounded-full bg-pink-100 text-pink-700 text-[10px] font-medium">{entity}</span>
                  ))}
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => { setViewTab('compartir'); }}
                className="p-5 rounded-2xl bg-primary-100/70 border border-primary-200/50 hover:border-primary-300/60 transition-all duration-200 text-left cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center shrink-0">
                    <i className="ri-share-forward-line text-xl text-white"></i>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-foreground-950">Compartir Contenido</p>
                    <p className="text-[11px] text-foreground-500">+15 pts por cada contenido compartido</p>
                  </div>
                </div>
              </button>
              <button
                onClick={() => { setInviteModalOpen(true); }}
                className="p-5 rounded-2xl bg-accent-100/70 border border-accent-200/50 hover:border-accent-300/60 transition-all duration-200 text-left cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-accent-500 flex items-center justify-center shrink-0">
                    <i className="ri-user-add-line text-xl text-white"></i>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-foreground-950">Invitar a Alguien</p>
                    <p className="text-[11px] text-foreground-500">+150 a +500 pts por referido exitoso</p>
                  </div>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* ═══ COMPARTIR ═══ */}
        {viewTab === 'compartir' && (
          <div className="space-y-8">
            {/* Share Platforms */}
            <div>
              <h3 className="text-sm font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-share-forward-line text-primary-600"></i>
                Plataformas de Compartir
              </h3>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                {sharePlatforms.map((platform) => (
                  <button
                    key={platform.id}
                    onClick={() => handleShare(platform.id)}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl border border-background-200/70 hover:border-primary-200/60 transition-all duration-200 cursor-pointer ${platform.bgColor}`}
                  >
                    <div className="w-12 h-12 rounded-full bg-background-50 flex items-center justify-center">
                      <i className={`${platform.icon} text-xl ${platform.color}`}></i>
                    </div>
                    <p className="text-[10px] font-semibold text-foreground-800">{platform.label}</p>
                    <p className="text-[9px] text-foreground-500 text-center">{platform.description}</p>
                  </button>
                ))}
              </div>
              {shareCopied && (
                <div className="mt-3 text-center">
                  <span className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">
                    <i className="ri-check-line"></i> ¡Enlace copiado!
                  </span>
                </div>
              )}
            </div>

            {/* Most Shared */}
            <div>
              <h3 className="text-sm font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <i className="ri-fire-line text-red-500"></i>
                Lo Más Compartido
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {mostSharedEntities.map((entity) => (
                  <Link
                    key={entity.id}
                    to={entity.linkUrl}
                    className="rounded-2xl bg-background-100 border border-background-200/70 overflow-hidden hover:border-primary-300/60 transition-all duration-300 group"
                  >
                    <div className="relative">
                      <img src={entity.imageUrl} alt={entity.name} className="w-full h-32 object-cover object-top" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                      <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-background-50/90 text-foreground-800 text-[9px] font-semibold border border-background-200/50 backdrop-blur-sm">
                        {entity.type}
                      </span>
                      <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full bg-primary-500/90 text-white text-[9px] font-bold backdrop-blur-sm">
                        {formatShareCount(entity.totalShares)}
                      </span>
                    </div>
                    <div className="p-3">
                      <p className="text-xs font-bold text-foreground-900 leading-snug line-clamp-1">{entity.name}</p>
                      <p className="text-[10px] text-foreground-500 line-clamp-1">{entity.subtitle}</p>
                      <div className="flex items-center gap-1.5 mt-2">
                        <i className={`${getPlatformIcon(entity.topPlatform)} ${getPlatformColor(entity.topPlatform)} text-[10px]`}></i>
                        <span className="text-[9px] text-foreground-500">{getPlatformLabel(entity.topPlatform)} lidera</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ═══ REFERIDOS ═══ */}
        {viewTab === 'referidos' && (
          <div className="space-y-6">
            {/* Type Filters */}
            <div className="flex flex-wrap items-center gap-1.5">
              {[
                { id: 'todos' as const, label: 'Todos', icon: 'ri-grid-line' },
                ...referralTypes.map((t) => ({ id: t.id, label: t.label, icon: t.icon, color: t.color, bg: t.bgColor })),
              ].map((tab) => {
                const isActive = referralFilter === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setReferralFilter(tab.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap border cursor-pointer ${
                      isActive
                        ? `border-current shadow-sm ${tab.id !== 'todos' ? (tab as { color: string; bg: string }).color + ' ' + (tab as { color: string; bg: string }).bg : 'text-foreground-700 bg-background-100 border-background-300'}`
                        : 'border-background-200 bg-background-50 text-foreground-500 hover:text-foreground-700 hover:border-background-300'
                    }`}
                  >
                    <i className={`${tab.icon} text-[10px]`}></i>
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Status Filters */}
            <div className="flex flex-wrap items-center gap-1.5">
              {[
                { id: 'todos' as const, label: 'Todos los Estados', icon: 'ri-grid-line' },
                { id: 'pendiente' as const, label: 'Pendientes', icon: 'ri-time-line', color: 'text-amber-600 bg-amber-50 border-amber-200' },
                { id: 'aceptada' as const, label: 'Aceptadas', icon: 'ri-check-line', color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
                { id: 'rechazada' as const, label: 'Rechazadas', icon: 'ri-close-line', color: 'text-red-500 bg-red-50 border-red-200' },
              ].map((tab) => {
                const isActive = statusFilter === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setStatusFilter(tab.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap border cursor-pointer ${
                      isActive
                        ? `shadow-sm ${tab.id !== 'todos' ? tab.color : 'text-foreground-700 bg-background-100 border-background-300'}`
                        : 'border-background-200 bg-background-50 text-foreground-500 hover:text-foreground-700'
                    }`}
                  >
                    <i className={`${tab.icon} text-[10px]`}></i>
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Invite Button */}
            <button
              onClick={() => setInviteModalOpen(true)}
              className="inline-flex items-center gap-2 px-5 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap cursor-pointer"
            >
              <i className="ri-user-add-line"></i>
              Nueva Invitación
            </button>

            {/* Results */}
            <p className="text-[11px] text-foreground-400">
              {filteredRecords.length === 0 ? 'No se encontraron invitaciones.' : `Mostrando ${filteredRecords.length} invitacion${filteredRecords.length !== 1 ? 'es' : ''}`}
            </p>

            {filteredRecords.length > 0 ? (
              <div className="space-y-3">
                {filteredRecords.map((record) => (
                  <div
                    key={record.id}
                    className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200/60 transition-all duration-200"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${referralTypes.find((t) => t.id === record.referralType)?.bgColor || 'bg-background-50'}`}>
                        <i className={`${referralTypes.find((t) => t.id === record.referralType)?.icon || 'ri-user-add-line'} text-lg ${referralTypes.find((t) => t.id === record.referralType)?.color || 'text-foreground-500'}`}></i>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className="text-sm font-bold text-foreground-900">{record.invitedName}</p>
                          {getStatusBadge(record.status)}
                        </div>
                        <div className="flex items-center gap-2 text-[10px] text-foreground-500">
                          {getTypeBadge(record.referralType)}
                          <span>{record.invitedEmail}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-[11px] shrink-0">
                      <span className="text-foreground-500">{new Date(record.sentDate).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}</span>
                      {record.scoreEarned && (
                        <span className="flex items-center gap-1 text-primary-600 font-semibold">
                          <i className="ri-flashlight-line text-[10px]"></i>+{record.scoreEarned} pts
                        </span>
                      )}
                      {record.rewardUnlocked && (
                        <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-[9px] font-medium">{record.rewardUnlocked}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mb-4">
                  <i className="ri-user-unfollow-line text-2xl text-foreground-300"></i>
                </div>
                <p className="text-sm font-semibold text-foreground-400">No se encontraron invitaciones</p>
                <p className="text-[12px] text-foreground-300 mt-1">Intentá ajustar los filtros o enviá una nueva invitación</p>
              </div>
            )}
          </div>
        )}

        {/* ═══ RECOMPENSAS ═══ */}
        {viewTab === 'recompensas' && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {referralRewards.map((reward) => (
                <div
                  key={reward.id}
                  onClick={() => setSelectedReward(selectedReward === reward.id ? null : reward.id)}
                  className={`rounded-2xl border transition-all duration-300 cursor-pointer ${
                    reward.unlocked
                      ? 'bg-background-100 border-background-200/70 hover:border-accent-200/60'
                      : 'bg-background-50 border-background-200/50 opacity-80 hover:opacity-100 hover:border-primary-200/60'
                  } p-5`}
                >
                  <div className="text-center mb-3">
                    <span className="text-3xl">{reward.emoji}</span>
                  </div>
                  <p className="text-sm font-bold text-foreground-900 text-center mb-1">{reward.name}</p>
                  <p className="text-[10px] text-foreground-500 text-center mb-3 line-clamp-2">{reward.description}</p>

                  {/* Progress Bar */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between text-[9px] mb-1">
                      <span className="text-foreground-500">{reward.requirement}</span>
                      <span className={`font-semibold ${reward.unlocked ? 'text-emerald-600' : 'text-foreground-500'}`}>
                        {reward.progress}%
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-background-200 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${reward.unlocked ? 'bg-emerald-500' : 'bg-primary-400'}`}
                        style={{ width: `${reward.progress}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="flex items-center justify-center gap-1.5">
                    <span className={`px-2 py-0.5 rounded-full text-[9px] font-semibold ${
                      reward.type === 'puntos' ? 'bg-primary-100 text-primary-700' :
                      reward.type === 'insignia' ? 'bg-accent-100 text-accent-700' :
                      reward.type === 'beneficio' ? 'bg-violet-100 text-violet-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {reward.type === 'puntos' ? `+${reward.pointsValue} pts` : reward.type}
                    </span>
                    {reward.unlocked && (
                      <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold">
                        <i className="ri-check-line text-[8px]"></i> Desbloqueado
                      </span>
                    )}
                  </div>

                  {/* Expanded detail */}
                  {selectedReward === reward.id && (
                    <div className="mt-4 pt-4 border-t border-background-200/70">
                      <p className="text-[10px] text-foreground-600 leading-relaxed">{reward.description}</p>
                      {reward.type === 'puntos' && (
                        <p className="text-[10px] font-bold text-primary-600 mt-2">Sumás {reward.pointsValue} puntos al alcanzar este hito.</p>
                      )}
                      {reward.type === 'vip' && (
                        <p className="text-[10px] font-bold text-amber-600 mt-2">Status VIP por 3 meses con todos los beneficios premium.</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ═══ RANKING ═══ */}
        {viewTab === 'ranking' && (
          <div className="space-y-6">
            <div className="space-y-3">
              {topReferrers.map((referrer) => {
                const positionColors: Record<number, { border: string; bg: string; badge: string }> = {
                  1: { border: 'border-amber-300', bg: 'bg-amber-50/50', badge: 'bg-amber-100 text-amber-700' },
                  2: { border: 'border-foreground-300', bg: 'bg-foreground-50/50', badge: 'bg-foreground-100 text-foreground-700' },
                  3: { border: 'border-amber-200', bg: 'bg-amber-50/30', badge: 'bg-amber-50 text-amber-700' },
                };
                const colors = positionColors[referrer.position] || { border: 'border-background-200', bg: '', badge: 'bg-background-100 text-foreground-500' };

                return (
                  <div
                    key={referrer.position}
                    className={`flex items-center gap-4 p-4 rounded-xl border ${colors.border} ${colors.bg} hover:border-primary-200/60 transition-all duration-200`}
                  >
                    <div className="relative shrink-0">
                      <div className={`absolute -top-1 -left-1 w-5 h-5 rounded-full border-2 border-background-50 flex items-center justify-center text-[9px] font-extrabold ${colors.badge}`}>
                        {referrer.position}
                      </div>
                      <img src={referrer.avatar} alt={referrer.name} className="w-12 h-12 rounded-full object-cover ring-2 ring-background-200" />
                      {referrer.position <= 3 && (
                        <span className="absolute -bottom-1 -right-1 text-sm">
                          {referrer.position === 1 ? '🥇' : referrer.position === 2 ? '🥈' : '🥉'}
                        </span>
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-sm font-bold text-foreground-900">{referrer.name}</p>
                        <span className="px-1.5 py-0.5 rounded-full text-[8px] font-bold bg-accent-100 text-accent-700">
                          {referrer.tier}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px]">
                        <span className="text-foreground-500">
                          <span className="font-semibold text-foreground-900">{referrer.acceptedReferrals}</span>/{referrer.totalReferrals} referidos
                        </span>
                        <span className="text-emerald-600 flex items-center gap-0.5">
                          <i className="ri-check-double-line text-[9px]"></i>
                          {formatConversionRate(referrer.conversionRate)}
                        </span>
                        <span className="text-primary-600 font-semibold flex items-center gap-0.5">
                          <i className="ri-flashlight-line text-[9px]"></i>
                          {formatShareCount(referrer.scoreEarned)} pts
                        </span>
                      </div>
                      {referrer.badges.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {referrer.badges.map((b) => (
                            <span key={b} className="px-1.5 py-0.5 rounded-full bg-accent-100 text-accent-700 text-[8px] font-medium">{b}</span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="text-right shrink-0">
                      <p className="text-lg font-extrabold text-foreground-950">{formatShareCount(referrer.scoreEarned)}</p>
                      <p className="text-[9px] text-foreground-500">pts RFW</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* SCORE SECTION — visible in Dashboard and Compartir */}
        {(viewTab === 'dashboard' || viewTab === 'compartir') && (
          <div className="rounded-3xl bg-background-100 border border-background-200/70 p-6 md:p-8">
            <div className="text-center mb-6">
              <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-3">
                <i className="ri-flashlight-line text-xl text-primary-600"></i>
              </div>
              <h3 className="text-lg font-extrabold text-foreground-950 mb-2">Score por Compartir y Referir</h3>
              <p className="text-xs text-foreground-500">Cada acción suma. Cada referido exitoso suma más. Compartir RFW te da puntos por cada plataforma.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {referralScoreActions.map((action, i) => (
                <div key={i} className="p-4 rounded-xl bg-background-50 border border-background-200/70 text-center">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-2">
                    <i className={`${action.icon} text-base ${action.color}`}></i>
                  </div>
                  <p className="text-xs font-bold text-foreground-900 mb-1">{action.action}</p>
                  <p className="text-lg font-extrabold text-primary-600 mb-1">+{action.points} pts</p>
                  <p className="text-[10px] text-foreground-500 leading-snug">{action.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CROSS LINKS */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to="/rankings" className="p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-accent-200/60 transition-all duration-200 text-center">
            <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-2">
              <i className="ri-trophy-line text-base text-accent-600"></i>
            </div>
            <p className="text-sm font-semibold text-foreground-900">Rankings RFW</p>
            <p className="text-[10px] text-foreground-500 mt-1">Los más compartidos en cada categoría</p>
          </Link>
          <Link to="/seguidores" className="p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200/60 transition-all duration-200 text-center">
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-2">
              <i className="ri-user-heart-line text-base text-primary-600"></i>
            </div>
            <p className="text-sm font-semibold text-foreground-900">Seguidores RFW</p>
            <p className="text-[10px] text-foreground-500 mt-1">Seguí y compartí tus entidades favoritas</p>
          </Link>
          <Link to="/publicidad" className="p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-pink-300/60 transition-all duration-200 text-center">
            <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center mx-auto mb-2">
              <i className="ri-money-dollar-circle-line text-base text-pink-600"></i>
            </div>
            <p className="text-sm font-semibold text-foreground-900">Publicidad RFW</p>
            <p className="text-[10px] text-foreground-500 mt-1">Monetizá tu alcance y tus referidos</p>
          </Link>
        </section>

        {/* REGLA FINAL */}
        <section className="p-6 rounded-2xl bg-primary-100/70 border border-primary-200/50 text-center">
          <div className="w-12 h-12 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-share-forward-line text-xl text-white"></i>
          </div>
          <p className="text-lg font-extrabold text-foreground-950 mb-2">Compartir debe existir en toda la plataforma.</p>
          <p className="text-sm text-foreground-600 max-w-xl mx-auto">
            Cada contenido, cada radio, cada programa, cada evento — todo puede y debe compartirse. No es solo difusión: es construir comunidad, traer nuevos oyentes, invitar radios, conectar locutores. Un referido exitoso es la mayor muestra de confianza en RFW. Compartir no es opcional — es parte del ADN de la plataforma.
          </p>
        </section>
      </main>

      {/* ═══ INVITE MODAL ═══ */}
      {inviteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => { if (!sendSuccess) setInviteModalOpen(false); }}></div>
          <div className="relative bg-background-50 rounded-3xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl p-5 md:p-6">
            <button
              onClick={() => setInviteModalOpen(false)}
              className="absolute top-4 right-4 z-20 w-8 h-8 rounded-full bg-background-100 flex items-center justify-center hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-line text-foreground-700"></i>
            </button>

            {sendSuccess ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-double-line text-3xl text-emerald-500"></i>
                </div>
                <h3 className="text-lg font-extrabold text-foreground-950 mb-2">¡Invitación Enviada!</h3>
                <p className="text-sm text-foreground-500">Tu invitación fue enviada con éxito. Recibirás una notificación cuando sea aceptada.</p>
              </div>
            ) : (
              <>
                <h3 className="text-lg font-extrabold text-foreground-950 mb-1">Nueva Invitación</h3>
                <p className="text-sm text-foreground-500 mb-5">Invitá a alguien a unirse a RFW y ganá puntos cuando acepte.</p>

                {/* Referral Type Selector */}
                <div className="grid grid-cols-2 gap-2 mb-5">
                  {referralTypes.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => setInviteType(type.id)}
                      className={`flex items-center gap-2 p-3 rounded-xl border transition-all duration-200 text-left cursor-pointer ${
                        inviteType === type.id
                          ? `${type.bgColor} border-current ${type.color}`
                          : 'bg-background-100 border-background-200/70 hover:border-background-300'
                      }`}
                    >
                      <i className={`${type.icon} text-base ${type.color}`}></i>
                      <div>
                        <p className="text-xs font-bold text-foreground-900">{type.label}</p>
                        <p className={`text-[10px] ${type.color} font-semibold`}>+{type.scoreReward} pts</p>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Form */}
                <div className="space-y-4 mb-5">
                  <div>
                    <label className="block text-xs font-semibold text-foreground-700 mb-1">Nombre</label>
                    <input
                      type="text"
                      value={inviteName}
                      onChange={(e) => setInviteName(e.target.value)}
                      placeholder="Nombre de la persona o entidad"
                      className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground-700 mb-1">Email o Teléfono</label>
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="email@ejemplo.com"
                      className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground-700 mb-1">Mensaje (opcional)</label>
                    <textarea
                      value={inviteMessage}
                      onChange={(e) => setInviteMessage(e.target.value)}
                      placeholder="Escribí un mensaje personalizado..."
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 resize-none h-24"
                    ></textarea>
                    <p className="text-[9px] text-foreground-400 mt-1 text-right">{inviteMessage.length}/500</p>
                  </div>
                </div>

                <button
                  onClick={handleInvite}
                  disabled={!inviteName.trim() || !inviteEmail.trim()}
                  className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-full text-sm font-bold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    inviteName.trim() && inviteEmail.trim()
                      ? 'bg-primary-500 text-white hover:bg-primary-600'
                      : 'bg-background-200 text-foreground-400 cursor-not-allowed'
                  }`}
                >
                  <i className="ri-send-plane-line"></i>
                  Enviar Invitación
                </button>

                <p className="text-[10px] text-foreground-400 text-center mt-3">
                  Ganás {referralTypes.find((t) => t.id === inviteType)?.scoreReward || 0} puntos cuando la invitación sea aceptada.
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}